/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { FC } from "react";

import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import {
  FormType,
  FulfillmentMethod,
  OfferedFulfillmentMethod,
  formatCurrencyAmount,
  getDollarValue,
} from "@simplyk/common";
import { Controller, useFormContext } from "react-hook-form";

import { DeliveryMethodCard } from "./DeliveryMethodCard";
import { PickupAddressInfobox } from "./PickupAddressInfobox";

import { SectionWrapper } from "@/canvas/features/FormV2/blocks/payment/SectionWrapper";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormInfobox } from "@/forms-design-system/FormInfobox";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Delivery, Location, Warning } from "@/icons/outlined";

export const FulfillmentSection: FC = () => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { formCurrency, commandFulfillment: visibility } = useFormV2Context(FormType.Ticketing);
  const { control, watch } = useFormContext<TicketingPaymentInput>();
  const fulfillmentMethod = watch("fulfillmentMethod");
  const showPickupAddress =
    fulfillmentMethod === FulfillmentMethod.Pickup ||
    (visibility.pickupOnly && visibility.offeredMethods.includes(OfferedFulfillmentMethod.Pickup));

  if (!visibility.showFulfillmentOnPayment) {
    return null;
  }

  const shippingFeeLabel = formatCurrencyAmount({
    value: getDollarValue(visibility.shippingFeeAmount),
    currency: formCurrency,
    locale,
  });

  return (
    <Stack gap={3} width="100%" id="fulfillment-section" data-test="fulfillment-section">
      {(visibility.showDeliveryMethodChoice || visibility.pickupOnly) && (
        <SectionWrapper
          title={
            visibility.pickupOnly
              ? t("ticketing", "fulfillment.pickupInfo")
              : t("ticketing", "fulfillment.deliveryMethod")
          }
          dataTest="fulfillment-method-section"
        >
          <Stack gap={2}>
            {visibility.showDeliveryMethodChoice && (
              <Controller
                control={control}
                name="fulfillmentMethod"
                rules={isRequired(t("ticketing", "fulfillment.deliveryMethodRequired"))}
                render={({ field, fieldState }) => (
                  <Stack
                    ref={field.ref}
                    tabIndex={-1}
                    aria-invalid={Boolean(fieldState.error)}
                    data-test="fulfillment-method-control"
                    gap={2}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      alignItems={{ xs: "stretch", md: "center" }}
                      gap={1.5}
                    >
                      {visibility.offeredMethods.includes(OfferedFulfillmentMethod.Pickup) && (
                        <DeliveryMethodCard
                          selected={field.value === FulfillmentMethod.Pickup}
                          onSelect={() => field.onChange(FulfillmentMethod.Pickup)}
                          icon={
                            <FormIcon size="medium">
                              <Location className="svg-safari-fix" />
                            </FormIcon>
                          }
                          label={t("ticketing", "fulfillment.pickupOption")}
                          data-test="fulfillment-pickup-option"
                        />
                      )}
                      {visibility.offeredMethods.includes(OfferedFulfillmentMethod.Pickup) &&
                        visibility.offeredMethods.includes(OfferedFulfillmentMethod.Shipping) && (
                          <FormTypography
                            variant="body2"
                            vibe="quiet"
                            sx={{ textAlign: "center", flexShrink: 0, display: { xs: "none", md: "block" } }}
                          >
                            {t("ticketing", "fulfillment.deliveryOr")}
                          </FormTypography>
                        )}
                      {visibility.offeredMethods.includes(OfferedFulfillmentMethod.Shipping) && (
                        <DeliveryMethodCard
                          selected={field.value === FulfillmentMethod.Shipping}
                          onSelect={() => field.onChange(FulfillmentMethod.Shipping)}
                          icon={
                            <FormIcon size="medium">
                              <Delivery className="svg-safari-fix" />
                            </FormIcon>
                          }
                          label={t("ticketing", "fulfillment.shipOption", { fee: shippingFeeLabel })}
                          data-test="fulfillment-shipping-option"
                        />
                      )}
                    </Stack>
                    {fieldState.error?.message && (
                      <FormInfobox
                        role="alert"
                        icon={<Warning />}
                        variant="error"
                        description={fieldState.error.message}
                        data-test="fulfillment-method-error"
                      />
                    )}
                  </Stack>
                )}
              />
            )}
            <Collapse in={showPickupAddress} unmountOnExit timeout={300} easing="ease-in">
              <PickupAddressInfobox />
            </Collapse>
          </Stack>
        </SectionWrapper>
      )}
    </Stack>
  );
};
